// src/pages/ContactPage.jsx
import React, { useState } from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';

/**
 * ContactPage component provides a form for users to send messages
 * and displays contact information.
 */
function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [status, setStatus] = useState('');

  // Handle input changes for the form fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus('Sending...'); // Set status to indicate submission is in progress
    // Simulate API call
    setTimeout(() => {
      console.log('Form Data Submitted:', formData);
      setStatus('Message sent successfully!'); // Set success message
      setFormData({ name: '', email: '', message: '' }); // Clear form fields
      setTimeout(() => setStatus(''), 3000); // Clear status after 3 seconds
    }, 1500);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900 text-white p-4">
      <div className="bg-gray-800 p-8 rounded-lg shadow-xl text-center max-w-4xl w-full border border-gray-700">
        <h1 className="text-4xl font-bold text-blue-400 mb-6">
          Contact Us
        </h1>
        <p className="text-gray-300 mb-8">
          Have questions or feedback? We'd love to hear from you!
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Contact Information Section */}
          <div className="text-left">
            <h2 className="text-2xl font-semibold text-blue-300 mb-4">Our Details</h2>
            <ul className="space-y-4 text-gray-300">
              <li className="flex items-center gap-3">
                <Mail size={20} className="text-blue-400" />
                <span>info@apartmenttour.com</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={20} className="text-blue-400" />
                <span>+1 (123) 456-7890</span>
              </li>
              <li className="flex items-center gap-3">
                <MapPin size={20} className="text-blue-400" />
                <span>123 Virtual St, Digital City, VC 12345</span>
              </li>
            </ul>
            <div className="mt-6">
              <img
                src="https://placehold.co/400x250/334155/E2E8F0?text=Location+Map"
                alt="Placeholder Map"
                className="w-full h-auto rounded-lg shadow-md"
              />
            </div>
          </div>

          {/* Contact Form Section */}
          <div className="text-left">
            <h2 className="text-2xl font-semibold text-blue-300 mb-4">Send Us a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-gray-300 text-sm font-medium mb-1">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 rounded-md bg-gray-700 border border-gray-600 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Your Name"
                  required
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-gray-300 text-sm font-medium mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 rounded-md bg-gray-700 border border-gray-600 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="your.email@example.com"
                  required
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-gray-300 text-sm font-medium mb-1">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-2 rounded-md bg-gray-700 border border-gray-600 text-white placeholder-gray-400 resize-y focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Your message..."
                  required
                ></textarea>
              </div>
              <button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md shadow-lg transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
              >
                Send Message
              </button>
              {status && (
                <p className="mt-4 text-center text-sm text-blue-400">
                  {status}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactPage;
